import React, { useState, useEffect } from "react";
import moment from "moment";

//#region Charting Facilities
import { Line } from "react-chartjs-2";
import { CategoryScale, Chart as ChartJS, ChartDataset, Plugin, TooltipItem, TooltipCallbacks } from "chart.js/auto";
ChartJS.register(CategoryScale);
//#endregion Charting Facilities

import CustomDropDown from "input/CustomDropDown";
import { renderLoader } from "utility/LoaderUtils";
import { getVisitDuration } from "usage/VisitUtils";
import { ActivityGuideLink } from "utility/ActivityGuides";

enum ActivityChartType {
    Completions = 0,
    Scores = 1
}

const ActivityPanel = ({
    coachingPlanDataFound,
    currentWeek,
    dataMissing,
    isLoading,
    rawActivityData
}) => {
    const [activityCompletionsChartData, setActivityCompletionsChartData] = useState([]);
    const [activityData, setActivityData] = useState(null);
    const [activityScoresChartData, setActivityScoresChartData] = useState([]);
    const [activityOptions, setActivityOptions] = useState<{}[]>(null);
    const [chartLabels, setChartLabels] = useState([]);
    const [chartType, setChartType] = useState<ActivityChartType>(ActivityChartType.Completions);
    const [includeWeekZero, setIncludeWeekZero] = useState<boolean>(false);
    const [isConfigured, setIsConfigured] = useState<boolean>(false);
    const [isMovementActivity, setIsMovementActivity] = useState<boolean>(false);
    const [selectedActivityId, setSelectedActivityId] = useState<string>(null);
    const [tooltipScoresLabels, setTooltipScoresLabels] = useState([]);

    // ---=== Utilities ===--- //

    //#region Charting Facilities
    // const customBackgroundCompletionsColorizerPlugin: Plugin<"line"> = {
    //     id: "customBackgroundCompletionsColorizerPlugin",
    //     beforeDraw: (chart, _args, _options) => {
    //         const {
    //             ctx,
    //             chartArea: { top, left, width, height }
    //         } = chart;
    //
    //         ctx.save();
    //         ctx.fillStyle = "rgba(0, 0, 0, 0)";
    //         ctx.fillRect(left, top, width, height);
    //         ctx.restore();
    //     }
    // };

    const customBackgroundScoresColorizerPlugin: Plugin<"line"> = {
        id: "customBackgroundScoresColorizerPlugin",
        beforeDraw: (chart, _args, _options) => {
            const {
                ctx,
                chartArea: { left, width },
                scales: { y }
            } = chart;

            ctx.save();
            ctx.fillStyle = "rgba(0, 255, 0, 0.2)";
            ctx.fillRect(left, y.getPixelForValue(5000), width, y.getPixelForValue(4000) - y.getPixelForValue(5000));
            ctx.fillStyle = "rgba(173, 255, 47, 0.3)";
            ctx.fillRect(left, y.getPixelForValue(4000), width, y.getPixelForValue(2500) - y.getPixelForValue(4000));
            ctx.fillStyle = "rgba(255, 255, 0, 0.2)";
            ctx.fillRect(left, y.getPixelForValue(2500), width, y.getPixelForValue(1000) - y.getPixelForValue(2500));
            ctx.fillStyle = "rgba(255, 165, 0, 0.2)";
            ctx.fillRect(left, y.getPixelForValue(1000), width, y.getPixelForValue(0) - y.getPixelForValue(1000));
            ctx.restore();
        }
    };

    const customBackgroundPlugin = {
        id: "customBackgroundPlugin",
        beforeDraw: (chart: ChartJS<"line">) => {
            if (!isMovementActivity || chartType === ActivityChartType.Completions) {
                return;
            }

            customBackgroundScoresColorizerPlugin.beforeDraw(chart, null, null)
        }
    };

    const customLinePlotPlugin: Plugin<"line"> = {
        id: "customLinePlotPlugin",
        beforeDraw(chart: ChartJS<"line">) {
            const dataset = chart.data?.datasets[0] as ChartDataset<"line", number[]>;
            const data = dataset?.data;

            if (!data || !Array.isArray(data)) {
                return;
            }

            const ctx = chart.ctx;
            let xAxis = chart.scales["x"];
            let yAxis = chart.scales["y"];

            let valueFrom: number | null = null;
            let valueFromIndex = 0;
            let xFrom: number | null = null;
            let yFrom: number | null = null;

            ctx.save();
            ctx.strokeStyle = dataset.borderColor as string;

            dataset.data.forEach((value, index) => {
                if (value != null) {
                    let x = xAxis.getPixelForTick(index);
                    let y = yAxis.getPixelForValue(value);
                    if (valueFrom != null) {
                        ctx.lineWidth = (dataset.borderWidth as number) ?? 2;
                        if (index - valueFromIndex > 1) {
                            ctx.setLineDash([5, 5]);
                        }
                        else {
                            ctx.setLineDash([]);
                        }
                        ctx.beginPath();
                        ctx.moveTo(xFrom, yFrom);
                        ctx.lineTo(x, y);
                        ctx.stroke();
                    }
                    valueFrom = value;
                    valueFromIndex = index;
                    xFrom = x;
                    yFrom = y;
                }
            });
            ctx.restore();
        }
    };
    //#endregion Charting Facilities

    const tooltipScoreLabelColors = (context: TooltipItem<"line">) => {
        let raw: number = context.raw as number;
        let color: string;

        switch (true) {
            case raw < 1000:
                color = "rgba(255, 165, 0, 0.2)";
                break;
            case raw < 2500:
                color = "rgba(255, 255, 0, 0.2)";
                break;
            case raw < 4000:
                color = "rgba(173,255,47,0.3)";
                break;
            default:
                color = "rgba(0,255,0, 0.2)";
                break;
        }
        return color;
    };

    const tooltipScoreEncouragement = (context: TooltipItem<"line">[]) => {
        let raw: number = context[0].raw as number;
        let level: string;

        switch (true) {
            case raw < 1000:
                level = "Keep Going ";
                break;
            case raw < 2500:
                level = "Good";
                break;
            case raw < 4000:
                level = "Great";
                break;
            default:
                level = "Excellent";
                break;
        }
        return level;
    };


    const tooltipCompletionsCallback: Partial<TooltipCallbacks<"line">> = {
        title: (context: TooltipItem<"line">[]) => {
            return [`Week ${context[0].label}`];
        },
        label: (context: TooltipItem<"line">) => {
            const completions = context.raw as number;
            return `Completions: ${completions}`;
        },
        footer: (context: TooltipItem<"line">[]) => {
            const week = parseInt(context[0].label);

            // Get motion time for the specific week (getVisitDuration returns milliseconds)
            let motionTimeMilliseconds = 0;
            if (rawActivityData) {
                rawActivityData.forEach(activity => {
                    if (activity.activity_id === selectedActivityId && activity.coaching_plan_week === week) {
                        motionTimeMilliseconds += getVisitDuration(activity);
                    }
                });
            }

            // Convert milliseconds to seconds, then to minutes and seconds format
            const motionTimeSeconds = motionTimeMilliseconds / 1000;
            const minutes = Math.floor(motionTimeSeconds / 60);
            const seconds = Math.floor(motionTimeSeconds % 60);
            const timeString = `${minutes > 0 ? minutes + "m " : ""}${seconds > 0 ? seconds + "s" : ""}`.trim() || "0s";
            return [`Motion Time: ${timeString}`];
        }
    };

    const tooltipScoresCallback: Partial<TooltipCallbacks<"line">> = {
        title: (context: TooltipItem<"line">[]) => {
            return [`Week ${context[0].label}`];
        },
        label: (context: TooltipItem<"line">) => {
            return ` ${tooltipScoresLabels[context.dataIndex].toString()}`;
        },
        labelPointStyle: function (_context: TooltipItem<"line">) {
            return {
                pointStyle: "rect" as const,
                rotation: 0
            };
        },
        labelColor: function (context: TooltipItem<"line">) {
            let color = tooltipScoreLabelColors(context);
            return {
                borderColor: color,
                backgroundColor: color,
                borderWidth: 2
            };
        },
        footer: (context: TooltipItem<"line">[]) => {
            return [tooltipScoreEncouragement(context)];
        }
    };

    const processRawActivityData = () => {
        if (rawActivityData) {
            let processedData = {};
            let activityNameIdMap = {};

            for (const a in rawActivityData) {
                const activity = rawActivityData[a];
                const activityId = activity.activity_id;
                const activityName = activity.activity_name;
                const activityType = activity.activity_type;
                const isCompleted = activity.status === "Completed";
                const score = activity.scores?.score ?? 0;
                const week = activity.coaching_plan_week;

                if (!(activityName in activityNameIdMap)) {
                    activityNameIdMap[activityName] = activityId;
                }

                if (!processedData[activityId]) {
                    processedData[activityId] = {
                        motionTime: week !== 0 ? getVisitDuration(activity) : 0,
                        weekZeroMotionTime: week === 0 ? getVisitDuration(activity) : 0,
                        starts: week !== 0 ? 1 : 0,
                        weekZeroStarts: week === 0 ? 1 : 0,
                        completions: week !== 0 ? (isCompleted ? 1 : 0) : 0,
                        weekZeroCompletions: week === 0 ? (isCompleted ? 1 : 0) : 0,
                        type: activityType,
                        count: { [week]: isCompleted ? 1 : 0 },
                        scores: activityType === 3 ? { [week]: score } : undefined
                    };
                }
                else {
                    processedData[activityId].motionTime += week !== 0 ? getVisitDuration(activity) : 0;
                    processedData[activityId].weekZeroMotionTime += week === 0 ? getVisitDuration(activity) : 0;
                    processedData[activityId].starts += (week !== 0 ? 1 : 0);
                    processedData[activityId].weekZeroStarts += (week === 0 ? 1 : 0);
                    processedData[activityId].completions += week !== 0 ? (isCompleted ? 1 : 0) : 0;
                    processedData[activityId].weekZeroCompletions += week === 0 ? (isCompleted ? 1 : 0) : 0;
                    if (activityType === 3 && (!processedData[activityId].scores[week] || processedData[activityId].scores[week] < score)) {
                        processedData[activityId].scores[week] = score;
                    }
                    processedData[activityId].count[week] = (processedData[activityId].count[week] || 0) + (isCompleted ? 1 : 0);
                }
            }

            let options = [];
            const sortedActivityNames = Object.keys(activityNameIdMap).sort();
            sortedActivityNames.forEach(
                (activityName: string) => {
                    options.push(
                        {
                            label: activityName,
                            value: activityNameIdMap[activityName]
                        }
                    );
                }
            );

            setActivityData(processedData);
            setActivityOptions(options);
            setSelectedActivityId(activityNameIdMap[sortedActivityNames[0]]);
        }
    };

    const configureChart = (activityType: string) => {
        if (activityData) {
            let completionsChartData = [];
            let scoreChartData = [];
            let scoreLabels = [];
            let weekLabels = [];

            for (let i = (includeWeekZero ? 0 : 1); i <= currentWeek; i++) {
                weekLabels.push(i);
                if (activityData[activityType]["type"] === 3) {
                    scoreChartData.push(activityData[activityType]["scores"][i] ? (activityData[activityType]["scores"][i] > 5000 ? 5000 : activityData[activityType]["scores"][i]) : null);
                    scoreLabels.push(activityData[activityType]["scores"][i] ? activityData[activityType]["scores"][i] : null);
                }
                completionsChartData.push(activityData[activityType]["count"][i] ? activityData[activityType]["count"][i] : null);
            }

            setActivityCompletionsChartData(completionsChartData);
            setActivityScoresChartData(scoreChartData);
            setTooltipScoresLabels(scoreLabels);
            setChartLabels(weekLabels);
        }

        setIsConfigured(true);
    };

    // ---=== useEffects ===--- //

    useEffect(
        () => {
            processRawActivityData();
        }, [rawActivityData]
    );

    useEffect(
        () => {
            if (selectedActivityId) {
                configureChart(selectedActivityId);
            }
        }, [includeWeekZero, selectedActivityId]
    );

    useEffect(
        () => {
            if (selectedActivityId) {
                const isMovement = activityData[selectedActivityId]["type"] === 3;
                setIsMovementActivity(isMovement);
                if (!isMovement) {
                    setChartType(ActivityChartType.Completions);
                }
            }
        }, [selectedActivityId]
    );

    // ---=== Render ===--- //

    const renderTable = () => {
        let table = [];
        let starts = activityData[selectedActivityId]["starts"] + (includeWeekZero ? activityData[selectedActivityId]["weekZeroStarts"] : 0);
        let completions = activityData[selectedActivityId]["completions"] + (includeWeekZero ? activityData[selectedActivityId]["weekZeroCompletions"] : 0);
        let total = activityData[selectedActivityId]["motionTime"] + (includeWeekZero ? activityData[selectedActivityId]["weekZeroMotionTime"] : 0);
        let avg = (total / starts);

        let totalString = total > 0
            ? `${Math.floor(moment.duration(total).asHours()) > 0 ? (Math.floor(moment.duration(total).asHours()) + "h and ") : ""}${moment.duration(total).minutes() ? moment.duration(total).minutes() + "m " : ""}  ${moment.duration(total).seconds() > 0 ? moment.duration(total).seconds() + "s" : ""}`
            : `no data found`;
        let avgString = avg > 0 ? `${Math.floor(moment.duration(avg).asHours()) > 0 ? (Math.floor(moment.duration(avg).asHours()) + "h and ") : ""}${moment.duration(avg).minutes() ? moment.duration(avg).minutes() + "m " : ""}  ${moment.duration(avg).seconds() > 0 ? moment.duration(avg).seconds() + "s" : ""}` : `no data found`;

        table.push(
            <React.Fragment key={`stats-line-1`}>
                <tr className="border-y-0 even:bg-white odd:bg-white border-gray-200">
                    <td className="w-1/2 border-x-0 border-t-0 border-b-0 px-6 py-3 text-start text-xs font-medium text-gray-500  leading-[18px]">
                        <span className="text-gray-900">Starts</span>
                        <div className="float-end">{starts}</div>
                    </td>
                    <td className="w-1/2 border-x-0 border-t-0 border-b-0 px-6 py-3 text-start text-xs font-medium text-gray-500 leading-[18px]">
                        <span className="text-gray-900">Total Motion Time</span>
                        <div className="float-end">{totalString}</div>
                    </td>
                </tr>
            </React.Fragment>
        );
        table.push(
            <React.Fragment key={`stats-line-2`}>
                <tr className="border-y-2 even:bg-white odd:bg-white border-gray-200">
                    <td className="w-1/2 border-x-0 px-6 py-3 text-start text-xs font-medium text-gray-500 leading-[18px]">
                        <span className="text-gray-900">Completions</span>
                        <div className="float-end">{completions}</div>
                    </td>
                    <td className="w-1/2 border-x-0 px-6 py-3 text-start text-xs font-medium text-gray-500 leading-[18px]">
                        <span className="text-gray-900">Average Motion Time</span>
                        <div className="float-end">{avgString}</div>
                    </td>
                </tr>
            </React.Fragment>
        );

        return table;
    };

    return (
        <React.Fragment>
            {
                (isLoading || !isConfigured) && !dataMissing
                    ? (
                        renderLoader("Loading Activity Data", 50)
                    )
                    : (
                        dataMissing
                            ? (
                                <div className="w-full h-full justify-center gap-6 items-center inline-flex flex-col">
                                    <div className="w-14 h-14 p-3.5 bg-[#ffe9e2] rounded-full border border-gray-50 justify-center items-center inline-flex">
                                        <img className="w-7 h-7 relative flex-col justify-start items-start flex" src="/icons/data.svg" alt="icon"/>
                                    </div>
                                    <div className="text-wrap w-[80%] break-words">
                                        <div className="self-stretch text-center text-gray-700 text-md font-semibold leading-[18px]">No Activity History Available</div>
                                    </div>
                                    <div className="break-words inline-block text-center text-gray-500 text-base font-normal">
                                        {
                                            coachingPlanDataFound
                                                ? (
                                                    <>
                                                        Coaching plan active. No Activities recorded yet.
                                                    </>
                                                )
                                                : (
                                                    <>
                                                    No active coaching plan found.
                                                    </>
                                                )
                                        }
                                    </div>
                                </div>
                            )
                            : (
                                <div className="w-full h-[90%] justify-start items-center inline-flex flex-col">
                                    <div className="w-full h-[10%] gap-5 inline-flex justify-start items-center">
                                        <div className="grow shrink basis-0 ml-[26px] flex items-center flex-wrap">
                                            <span className="text-xl font-semibold text-gray-900 whitespace-nowrap">Activity History</span>
                                            <div className="flex items-center gap-2 ml-4">
                                                <input
                                                    className="w-4 h-4 p-0.5 rounded border border-gray-300 cursor-pointer"
                                                    type="checkbox"
                                                    checked={includeWeekZero}
                                                    onChange={() => setIncludeWeekZero(prev => !prev)}
                                                />
                                                <span className="text-xs font-medium text-gray-500 whitespace-nowrap">Include Week 0</span>
                                            </div>
                                        </div>
                                        {
                                            isMovementActivity && (
                                                <div className="rounded-lg h-[42px] shadow-sm border border-gray-300 justify-start items-start inline-flex">
                                                    <button
                                                        className={`w-[40%] h-full px-4 py-2.5 border-r rounded-l-lg border-gray-300 justify-center items-center gap-2 flex cursor-pointer${chartType === ActivityChartType.Scores ? " bg-brand-100 " : " bg-white"}${chartType === ActivityChartType.Completions ? " hover:bg-gray-100" : ""}`}
                                                        onClick={() => {setChartType(ActivityChartType.Scores);}}
                                                    >
                                                        <div className="text-gray-700 text-xs font-medium leading-[18px]">Score</div>
                                                    </button>
                                                    <button
                                                        className={`w-[60%] h-full px-4 py-2.5 border-r rounded-r-lg border-gray-300 justify-center items-center gap-2 flex cursor-pointer${chartType === ActivityChartType.Completions ? " bg-brand-100" : " bg-white"}${chartType === ActivityChartType.Scores ? " hover:bg-gray-100" : ""}`}
                                                        onClick={() => {setChartType(ActivityChartType.Completions);}}
                                                    >
                                                        <div className="text-gray-700 text-xs font-medium leading-[18px]">Completions</div>
                                                    </button>
                                                </div>
                                            )
                                        }
                                        <div className="inline-flex gap-2 justify-start items-center mr-[26px]">
                                            <CustomDropDown
                                                defaultValue={selectedActivityId}
                                                options={activityOptions}
                                                // Callbacks
                                                onSelectionCallback={setSelectedActivityId}
                                            />
                                            <ActivityGuideLink activityId={selectedActivityId}/>
                                        </div>
                                    </div>
                                    <div className={`w-[calc(100%-48px)] ${isMovementActivity ? "h-[65%] mt-5" : "mt-5 h-[65%]"}`}>
                                        <Line
                                            key={`chart-${selectedActivityId}=${chartType}`}
                                            data={
                                                {
                                                    labels: chartLabels,
                                                    datasets: [
                                                        {
                                                            label: chartType === ActivityChartType.Completions ? " Completions" : "Score",
                                                            data: chartType === ActivityChartType.Completions ? activityCompletionsChartData: activityScoresChartData,
                                                            fill: false,
                                                            borderColor: "#2196f3",
                                                            backgroundColor: "#2196f3",
                                                            borderWidth: 1,
                                                            spanGaps: true,
                                                            segment: {
                                                                borderDash: ctx => {
                                                                    return !activityScoresChartData[ctx.p0DataIndex] || !activityScoresChartData[ctx.p1DataIndex] ? [10, 19] : [];
                                                                },
                                                                borderColor: ctx => {
                                                                    return !activityScoresChartData[ctx.p0DataIndex] || !activityScoresChartData[ctx.p1DataIndex] ? "#808080" : "#2196f3";
                                                                }
                                                            }
                                                        }
                                                    ]
                                                }
                                            }
                                            options={
                                                {
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    clip: false,
                                                    plugins: {
                                                        tooltip: {
                                                            boxPadding: 3,
                                                            footerFont: { weight: "normal", style: "italic" },
                                                            padding: 6,
                                                            callbacks: chartType === ActivityChartType.Completions ? tooltipCompletionsCallback : tooltipScoresCallback
                                                        },
                                                        legend: {
                                                            display: false
                                                        }
                                                    },
                                                    scales: {
                                                        x: {
                                                            display: true,
                                                            grid: {
                                                                display: false
                                                            },
                                                            title: {
                                                                display: true,
                                                                text: "Week",
                                                                font: {
                                                                    family: "Inter"
                                                                }
                                                            }
                                                        },
                                                        y: {
                                                            beginAtZero: true,
                                                            max: chartType === ActivityChartType.Completions ? Math.max(...activityCompletionsChartData) + 1 : 5000,
                                                            ticks: {
                                                                stepSize: chartType ? 1000 : 1
                                                            },
                                                            display: true,
                                                            title: {
                                                                display: true,
                                                                text: chartType === ActivityChartType.Completions ? "Completion" : "Score",
                                                                font: {
                                                                    family: "Inter"
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                            plugins={
                                                [
                                                    customBackgroundPlugin,
                                                    customLinePlotPlugin
                                                ]
                                            }
                                        />
                                    </div>
                                    <div className="w-[calc(100%-48px)] border-0">
                                        <table className="w-full h-[100%] divide-gray-700 border-0  dark:divide-gray-700 rounded-[10px]">
                                            <tbody className="border-0">
                                                {renderTable()}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )
                    )
            }
        </React.Fragment>
    );
};

export default ActivityPanel;
